import React from 'react';
import { Card, Space, Tooltip, Typography } from 'antd';
import { ClockCircleOutlined, DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { SalesFunnelData } from '../../../types/dashboard';
import { formatNumber, formatPercent } from '../utils';

const { Text } = Typography;

interface SalesDetailFunnelCardProps {
  funnel: SalesFunnelData | null;
  loading: boolean;
  glassCardStyle: React.CSSProperties;
}

const SalesDetailFunnelCard: React.FC<SalesDetailFunnelCardProps> = ({
  funnel,
  loading,
  glassCardStyle
}) => {
  return (
    <Card
      title={
        <Space size={6}>
          <ClockCircleOutlined style={{ color: '#7BAA18' }} />
          <Text strong>Corong Konversi Personal</Text>
          <Tooltip title="Corong konversi melacak kesehatan alur kerja pelayanan sales agent. Persentase dihitung fase-demi-fase (Step-by-Step Conversion) untuk mempermudah identifikasi hambatan operasional tim.">
            <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
          </Tooltip>
        </Space>
      }
      bordered={false}
      className="glass-card-interactive"
      styles={{ header: { padding: '10px 20px', minHeight: 'auto' }, body: { padding: '12px 16px' } }}
      style={{ ...glassCardStyle, height: '510px', overflowY: 'auto' }}
      loading={loading || !funnel}
    >
      {funnel && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center', padding: '0' }}>
          {/* Stage 1: Total Leads */}
          <Tooltip title="Semua data calon pembeli (leads) aktif yang terdaftar masuk ke sistem pada periode dan kawasan yang difilter." placement="left">
            <div 
              className="funnel-stage-item"
              style={{
                width: '100%',
                background: 'rgba(100, 116, 139, 0.08)',
                border: '1px solid rgba(100, 116, 139, 0.2)',
                borderRadius: '8px',
                padding: '6px 12px',
                textAlign: 'center',
                boxShadow: 'inset 0 1px 3px rgba(255, 255, 255, 0.5)',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong style={{ color: '#475569' }}>1. Total Leads</Text>
                <Text strong style={{ fontSize: '15px' }}>{formatNumber(funnel.total_leads)}</Text>
              </div>
            </div>
          </Tooltip>

          {/* Chevron Separator */}
          <DownOutlined style={{ color: '#cbd5e1', fontSize: '10px', margin: '0' }} />

          {/* Stage 2: Followed Up */}
          <Tooltip title="Calon pembeli yang sudah mulai dihubungi oleh sales (memiliki riwayat follow-up atau status follow-up Cold, Hot, Prospect, RSV, OK). Tingkat Respon (Followed Up Rate) = Mulai Di-follow Up / Total Leads * 100%." placement="left">
            <div 
              className="funnel-stage-item"
              style={{
                width: '92%',
                background: 'rgba(24, 144, 255, 0.12)',
                border: '1px solid rgba(24, 144, 255, 0.25)',
                borderRadius: '8px',
                padding: '6px 12px',
                textAlign: 'center',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong style={{ color: '#096dd9' }}>2. Mulai Di-follow Up</Text>
                <div style={{ textAlign: 'right' }}>
                  <Text strong style={{ fontSize: '14px', display: 'block' }}>{formatNumber(funnel.followed_up)}</Text>
                  <Text type="secondary" style={{ fontSize: '11px', color: '#096dd9', fontWeight: 600 }}>{formatPercent(funnel.stages_percentage.followed_up_rate)}</Text>
                </div>
              </div>
            </div>
          </Tooltip>

          {/* Chevron Separator */}
          <DownOutlined style={{ color: '#cbd5e1', fontSize: '10px', margin: '0' }} />

          {/* Stage 3: Qualified */}
          <Tooltip title="Calon pembeli yang dinilai potensial oleh sales agent setelah dihubungi (status minat: minat proses, minat tidak pesan, minat pesan). Tingkat Kualifikasi (Qualification Rate) = Qualified / Mulai Di-follow Up * 100%." placement="left">
            <div 
              className="funnel-stage-item"
              style={{
                width: '84%',
                background: 'rgba(250, 173, 20, 0.12)',
                border: '1px solid rgba(250, 173, 20, 0.25)',
                borderRadius: '8px',
                padding: '6px 12px',
                textAlign: 'center',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong style={{ color: '#d46b08' }}>3. Prospek Layak</Text>
                <div style={{ textAlign: 'right' }}>
                  <Text strong style={{ fontSize: '14px', display: 'block' }}>{formatNumber(funnel.qualified)}</Text>
                  <Text type="secondary" style={{ fontSize: '11px', color: '#d46b08', fontWeight: 600 }}>{formatPercent(funnel.stages_percentage.qualification_rate)}</Text>
                </div>
              </div>
            </div>
          </Tooltip>

          {/* Chevron Separator */}
          <DownOutlined style={{ color: '#cbd5e1', fontSize: '10px', margin: '0' }} />

          {/* Stage 4: Reservation */}
          <Tooltip title="Calon pembeli yang sudah berkomitmen memesan unit dan membayar booking fee/uang tanda jadi (status RSV). Tingkat Reservasi (Reservation Rate) = Reservasi / Qualified * 100%." placement="left">
            <div 
              className="funnel-stage-item"
              style={{
                width: '76%',
                background: 'rgba(255, 120, 117, 0.12)',
                border: '1px solid rgba(255, 120, 117, 0.25)',
                borderRadius: '8px',
                padding: '6px 12px',
                textAlign: 'center',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong style={{ color: '#cf1322' }}>4. Reservasi (RSV)</Text>
                <div style={{ textAlign: 'right' }}>
                  <Text strong style={{ fontSize: '14px', display: 'block' }}>{formatNumber(funnel.reservation)}</Text>
                  <Text type="secondary" style={{ fontSize: '11px', color: '#cf1322', fontWeight: 600 }}>{formatPercent(funnel.stages_percentage.reservation_rate)}</Text>
                </div>
              </div>
            </div>
          </Tooltip>

          {/* Chevron Separator */}
          <DownOutlined style={{ color: '#cbd5e1', fontSize: '10px', margin: '0' }} />

          {/* Stage 5: Closing OK */}
          <Tooltip title="Transaksi pemesanan unit yang berkasnya telah lengkap, divalidasi, dan disetujui resmi oleh Kantor Pusat (status OK). Tingkat Keberhasilan Deal (Closing Rate) = Transaksi Sukses / Reservasi * 100%." placement="left">
            <div 
              className="funnel-stage-item"
              style={{
                width: '68%',
                background: 'rgba(82, 196, 26, 0.12)',
                border: '1px solid rgba(82, 196, 26, 0.25)',
                borderRadius: '8px',
                padding: '6px 12px',
                textAlign: 'center',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong style={{ color: '#389e0d' }}>5. Closing (O K)</Text>
                <div style={{ textAlign: 'right' }}>
                  <Text strong style={{ fontSize: '15px', color: '#389e0d', display: 'block' }}>{formatNumber(funnel.closing)}</Text>
                  <Text type="secondary" style={{ fontSize: '11px', color: '#389e0d', fontWeight: 600 }}>{formatPercent(funnel.stages_percentage.closing_rate)}</Text>
                </div>
              </div>
            </div>
          </Tooltip>

          {/* Conversion footer ratio */}
          <div style={{ marginTop: '10px', textAlign: 'center', width: '100%', borderTop: '1px solid rgba(0,0,0,0.05)', paddingTop: '10px' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Rasio Konversi Akhir (Leads ➜ Closing): <Text strong style={{ color: '#389e0d' }}>{formatPercent((funnel.closing / (funnel.total_leads || 1)) * 100)}</Text>
            </Text>
          </div>
        </div>
      )}
    </Card>
  );
};

export default SalesDetailFunnelCard;
